// SH1 — the shelf system, client mirror of the server's ROOM_FOR_TYPE (server/index.js).
// One definition of which room a saved report belongs to, so the room's shelf and the
// server's shelf endpoint can never disagree about what lives where.
import { magicNoun } from "./magicRoom.js";

export const ROOM_FOR_TYPE = {
  "birth-chart": "astrology",
  "personality": "astrology",
  "compatibility": "astrology",
  "find-your-type": "astrology",
  "mini-report": "astrology",
  "question-reading": "astrology",
  "prediction": "astrology",
  "astrology-report": "astrology",
  "bubble-reading": "astrology",
  // L11: the funnel reading is cast from her birth data through the house's own engines, so its
  // shelf is the astrology room's — mirrored here because this file's whole point is that the room's
  // shelf and the server's shelf endpoint can never disagree about what lives where.
  "funnel-reading": "astrology",
  // R17: written by the server and posted by the client, and in neither copy of this map until now.
  "astrology-tradition": "astrology",
  // AKASHIC-1: the five soul-record readings, saved under their own types.
  "akashic-soul-record": "akashic",
  "akashic-past-life-connections": "akashic",
  "akashic-soul-contracts": "akashic",
  "akashic-past-life-gifts": "akashic",
  "akashic-purpose-timeline": "akashic",
  // R30: the People-flow synastry ("You & <person>") saves as `relationship` and was in neither copy
  // — the same silence R17 found one line above, one type over.
  "relationship": "astrology",
  "numerology": "numerology",
  "tarot": "tarot",
  "tarot-spread": "tarot",
  "human-design": "human-design",
  "human-design-compat": "human-design",
  "astrocartography-map": "astrocartography",
  "astrocartography-location": "astrocartography",
  "astrocartography-relocation": "astrocartography",
  "dream": "dream",
  "palm-reading": "palmistry",
  "chant": "magic",
  "affirmation": "magic",
  "incantation": "magic",
  "petition": "magic",
  "keys-working": "magic",
  "moses-working": "magic",
  "herb-working": "magic",
  "cauldron-working": "magic",
  "spell": "magic",
  "oils-working": "magic",
  "note": "magic",
  "seal": "seals",
};

export function roomForType(type) {
  return ROOM_FOR_TYPE[String(type || "").trim()] || null;
}

// R17 — THE RECEIVING END OF THE `room` A GENERATION CARRIES, and it lives out here so a gate can
// IMPORT it. The server writes `room` onto every active and recent generation; these two tables are
// what the client does with it, and an unrecognised value is silence in both.
//
// SHELF_ROOMS (SH2) is the shelf drawer's room list — the rooms "Open it" can actually open. A
// generation tagged to a room that is not in here hands the member a drawer that cannot show her
// what she just paid for. Owner's list; palmistry's shelf is empty until PR1 builds that room.
// AKASHIC-1 (2026-09-01): the room joins the shelf list in the SAME round its generations are
// registered. R17 refused the push when the roster rows existed and this list did not — "Open it"
// would have dispatched a room the drawer cannot show, which is silence with no error state.
export const SHELF_ROOMS = ["astrology", "numerology", "tarot", "human-design", "astrocartography", "dream", "seals", "magic", "palmistry", "akashic"];

// Which room a PAGE is standing in, for the site-wide follower — it hides work belonging to the
// room the member is already in, ON THE PREMISE THAT THAT ROOM'S OWN STRIP SAYS IT (R218 below is
// what happens when the premise is false). The comparison is `g.room !== currentRoom`, so a null on
// EITHER side silently drops the row: that is how the five R17 kinds vanished the moment a member
// took the strip's own invitation to roam the house.
export const ROOM_FOR_PAGE = {
  astrology: "astrology", numerology: "numerology", tarot: "tarot", "human-design": "human-design",
  astrocartography: "astrocartography", dreams: "dream", palmistry: "palmistry", seals: "seals",
  "magic-room": "magic", "spell-book": "magic", akashic: "akashic",
};

// R218 — **THE DOCK HIDES A ROOM'S OWN WORK BECAUSE THAT ROOM'S STRIP SAYS IT, AND SEVEN OF NINE
// ROOMS HAVE NO STRIP.**
//
// N1-2 exists because a member who pressed Generate and walked away had nothing anywhere telling her
// it was still going, and its first recorded decision is *"it hides work belonging to the room you
// are standing in — that room's own strip already says it, and two things saying the same thing on
// one screen is noise."* **That sentence is a claim about the code, and the same decision names the
// fact that refutes it four lines above: `GenerationQueueStrip` is mounted in exactly TWO rooms.**
// Measured against this file's own rosters: `ROOM_FOR_PAGE` resolves NINE rooms, two of them draw a
// strip, and **28 of the 33 kinds `GENERATION_KINDS` can register belong to the seven that do not.**
//
// So for those seven the dock does not hand the row to a second surface — **it drops it on the
// floor.** A member standing in the Astrology room with a paid reading in flight is told nothing;
// worse, a member who walked away, saw the dock, and came back INTO the room to look watches it
// disappear. **Human Design is one of the seven, and Human Design is the room the owner reported
// N1-2 in.**
//
// **AND THE PARTITION FIXTURE PROVED THE PARTITION USING ONE OF THE SEVEN.** `funnel-capture.mjs`
// drives *"every generation reaches exactly one surface"* over a fabricated queue with the member
// standing in `"tarot"` — a room with no strip — so the complement it computed was against a surface
// that is not rendered anywhere. A guard answering an easier question than the thing it guards
// (R30/R95/R116/R117/R146b/R148/R150/R158/R160/D-5/W7-2/L19/CRYSTAL/R217), on the roster rather than
// in a room.
//
// **THE REPAIR IS THE PREMISE, NOT THE SEVEN ROOMS** (scope rule 4, R19). Mounting a strip in seven
// more rooms is seven member-visible additions, each needing its own `onOpen`; the dock already
// exists, is already mounted site-wide, and is already the surface built for exactly this. What is
// wrong is that its hiding rule reads a fact — *does this room draw its own strip* — that nothing
// declared. It is declared here, beside `ROOM_FOR_PAGE`, because this block is the RECEIVING end of
// the `room` a generation carries and that is what these tables are.
//
// **THE DIRECTION IS THE RULING (R19/R8-4).** A room this roster does not name keeps its work ON THE
// DOCK: over-showing costs a line she can see and report, over-hiding costs a paid reading she
// cannot. So an unreadable room, an empty string and `null` all answer FALSE — presence, never
// truthiness (R53/R136/R137) — and every one of them leaves the row visible.
//
// **AND IT IS STRICTLY BETTER IN EVERY STATE, WHICH IS WHY THE RULE WAS REPAIRED RATHER THAN
// REMOVED.** In a room that draws a strip nothing moves (the strip says it, the dock stays quiet, no
// double). In a room that draws none the dock now says it, where before nothing did. There is no
// state this makes worse — which is what a bare deletion of the hiding rule could not claim.
//
// The roster is held as a BICONDITIONAL against the `<GenerationQueueStrip room="…">` call sites the
// client really renders (R11/R31), so a room that starts drawing its own strip joins by EXISTING and
// one that stops drawing it leaves. Both directions cost something: a name here with no call site is
// the silence above, and a call site with no name here is the row announced twice.
export const QUEUE_STRIP_ROOMS = ["astrocartography", "seals"];

export function roomDrawsOwnQueue(room) {
  const want = String(room || "");
  return want !== "" && QUEUE_STRIP_ROOMS.includes(want);
}

// The room's warm name, for the "Save to this room's shelf" affordance and the shelf heading.
export const ROOM_LABELS = {
  astrology: "Astrology",
  numerology: "Numerology",
  tarot: "Tarot",
  "human-design": "Human Design",
  astrocartography: "Astrocartography",
  dream: "Dream Interpretation",
  palmistry: "Palmistry",
  seals: "Seals",
  magic: "Magic Room",
  // AKASHIC-1: without this, a member is told her reading is waiting on the shelf of "this room".
  akashic: "Akashic Records",
};
export function roomLabel(room) {
  return ROOM_LABELS[room] || "this room";
}

// N7-4 — THE TYPES A MEMBER MAKES rather than has read about her. What she MADE is called what SHE
// called it, and `shelfTitle` must never reformat her own words: if she names an oil "Draw It In ·
// Fast", that is its name, not a subject and a reading to be flipped around. Readings keep the SH1
// reformat, because there the subject really is a person and the reading really is a document.
// R28 — AND IT REACHED FOUR OF THE ELEVEN THINGS SHE CAN MAKE IN THAT ROOM.
//
// R17/R25's shape: a roster on the SERVER (`ROOM_FOR_TYPE` files ELEVEN types to room "magic"), a
// hand-typed Set on the CLIENT, and a receiving-side default that is not an error but a SILENT
// REFORMAT OF HER OWN WORDS. Measured, the seven that fell through split cleanly in two, which is
// what makes this a biconditional rather than a growing list of exceptions:
//
//   HER WORDS ARE IN THE TITLE, so the title is hers and is returned verbatim:
//     oils-working / spell / cauldron-working / note   `creationTitle(type, creationName)` — she named it
//     chant / affirmation / incantation                `${label}: ${situation}` — her situation
//     herb-working                                     `Herbs for ${aimLabel}` — her aim
//   THE SERVER COMPOSED IT IN SH1's OWN "<owner> · <working>" SHAPE, so the flip below is what SH1
//   ASKS FOR and these belong OUTSIDE the Set. They are named rather than implied (R23's lesson —
//   which rooms fall through must never be decided by the order of a ternary and written down nowhere):
//     petition       `${owner} · Petition` / `${owner} · Petition on ${who}`
//     keys-working   `${name} · Key of Solomon Working`
//     moses-working  `${name} · Books of Moses Working`
//
// Eight plus three is eleven, so every type this room can write is on ONE side BY NAME, and the next
// generator added cannot default into the reformat by being forgotten.
export const CREATION_TYPES = new Set([
  "oils-working", "spell", "cauldron-working", "note",
  "chant", "affirmation", "incantation", "herb-working",
]);

// The other side of that biconditional. Declared, not implied: `shelfTitle` treats "not a creation"
// as "flip it", and a set that exists only as the absence of another set cannot be checked.
export const COMPOSED_TITLE_TYPES = new Set(["petition", "keys-working", "moses-working"]);

// R110 — **THE SEPARATOR IS THE CROSSING, AND IT WAS TYPED BY HAND AT SIXTEEN CALL SITES WHILE THE
// PARSER THAT READS IT BACK SAT DOWN HERE.** `shelfTitle` splits a saved title on " · " to turn
// "<subject> · <reading>" into SH1's "<reading>, <first name>". Nothing carried that string between
// the two ends: a room composing its title with a colon, a comma or an en dash is not an error, it
// simply never gets reformatted, and the shelf quietly stops obeying SH1 for that one room. One
// declaration, both ends (R31: the crossing stops existing rather than being watched).
export const TITLE_JOIN = " · ";

// **AND THE RULE ABOUT WHAT MAY STAND ON THE SUBJECT SIDE OF IT, WHICH N7-4 ALREADY WROTE DOWN AND
// WAS TRUE OF THE SPELLBOOK AND OF NOTHING ELSE** (W12/R26/R35/R38's shape: the capability lives on
// the surface that needed it first). Its comment reads *"Never `You · <title>`, which the shelf read
// back as `<title>, You`"* — and TWO surfaces did exactly that, because a placeholder is not a
// subject: the bubble reading and all three astrocartography reports fell back to the English
// literal `"You"` on the subject side, so an unnamed report landed on her shelf as
// "Your Personal Geography, You".
//
// An absent subject yields the READING NAME ALONE. `shelfTitle` hands a title with no separator back
// verbatim, so the shelf row reads "Your Personal Geography" — which is what it is.
export function subjectReadingTitle(subject, reading) {
  const who = String(subject || "").trim();
  const what = String(reading || "").trim();
  if (!what) return who;
  if (!who) return what;
  return `${who}${TITLE_JOIN}${what}`;
}

// **THE THIRD SHAPE, AND ITS TWO COPIES HAD ALREADY DRIFTED.** A tarot reading is titled in SH1's
// finished form already ("Tarot, Universal"), so it never passes through the reformat — which is why
// it is composed rather than joined. Both readers hand-typed it, and the fallback for a draw with no
// spread label was "Your Own Spread" in one and "Reading" in the other: the same missing fact, two
// answers, on two rows of one shelf. **The fallback is the one a member could have meant** — a
// face-down draw with no named spread IS her own spread.
export const TAROT_SPREAD_FALLBACK = "Your Own Spread";

export function tarotShelfTitle(spreadLabel) {
  return `Tarot, ${String(spreadLabel || "").trim() || TAROT_SPREAD_FALLBACK}`;
}

// SH1 shelf-item title law: "reading name + subject first name" (e.g. "Maturity, Christina").
// Saved titles follow "<subject> · <reading>" (most rooms) or "<reading>: <situation>" (craft)
// or already-shaped forms ("Tarot, Universal"). Reformat the common case; leave the rest as-is.
export function shelfTitle(item) {
  const raw = String(item?.title || "").trim();
  // W12-2: an untitled Magic Room item is named for what it IS. "Your reading" on a spell card is
  // the same defect as the shelf label that read "Birth Chart", arriving through the empty-title door.
  if (!raw) return magicNoun(item?.type) || "Your reading";
  // N7-4: a thing she MADE is shown verbatim, always. This is the line that stops
  // "Alexandra · How to Spell Your Oil" ever being read back as "How to Spell Your Oil, Alexandra".
  if (CREATION_TYPES.has(String(item?.type || "").trim())) return raw;
  if (raw.includes(TITLE_JOIN)) {
    const [subject, reading] = raw.split(TITLE_JOIN);
    const first = String(subject || "").trim().split(/\s+/)[0] || subject;
    if (reading && first) return `${reading.trim()}, ${first}`;
  }
  return raw;
}
